/**
 * Tire tracks: persistent ruts cut into the ground you actually rode.
 *
 * OWNER: piece:vfx
 *
 * The line is swept from `WheelState.cx/cy/cz` — the contact patch the tire solver is
 * itself using — not from the chassis position projected downward. That distinction is
 * the whole reason the track lands where the tire did: the patch sits comHeight below
 * and, once the bike is leaned, comHeight*sin(lean) to the inside of the CoM, so a
 * chassis-projected track cuts the *inside* of every berm by up to 40 cm while the tire
 * is out on the wall.
 *
 * ---------------------------------------------------------------------------
 * R2 — why this file was rewritten, and what replaced what
 * ---------------------------------------------------------------------------
 *
 * R1 drew the ribbon with a *distance-scaled* depth cheat: up to 9 m of push along the
 * view ray plus up to 1.4 m of vertical lift. Both were inherited from the trail-tread
 * ribbon, where they are the right tool — that mesh has to stay visible out to two
 * kilometres, and out there the clipmap under it is a completely different surface from
 * the one it was swept off. A tire track is not that mesh. It measured as two failures
 * at once:
 *
 *  - **Invisible where it matters.** Under the wheel the bias was ~1 cm of lift, and the
 *    innermost clipmap ring samples at 0.5 m, so on ground with any chatter in it the
 *    drawn triangle sits centimetres above the analytic height the contact patch reports.
 *    The ribbon spent most of its near-field life *inside* the hill.
 *  - **A haze where it does not.** At 200 m the same expression moved every quad nine
 *    metres toward the eye and over a metre up. A ribbon that far off its own surface no
 *    longer projects anywhere near the ground it belongs to: it smears into long
 *    horizontal bands lying across the hillside, painting over scatter boulders.
 *
 * So the bias is now the boring, correct thing and there is no distance term anywhere
 * in it:
 *
 *  1. `polygonOffset` on the material — a real depth bias, applied in the depth unit the
 *     hardware actually compares in, which is what kills coplanar z-fighting.
 *  2. A fixed `TRACK_LIFT` along the *ground normal*, sized against the one real error
 *     that polygon offset cannot express: the clipmap reconstructing a 0.5 m cell of
 *     rough ground as a flat triangle. It is a constant, it is a few centimetres, and at
 *     a few centimetres a rut sits in the ground rather than over it.
 *  3. A fixed `TRACK_PUSH` toward the eye, for the steep-look-down case where a normal
 *     lift buys almost no depth.
 *
 * And because none of those three can hold a ribbon on a surface the clipmap is no
 * longer resolving, the track simply **stops** before that range: it fades out over
 * TRACK_FADE0..TRACK_FADE1 and past that the vertex shader throws the quad out of the
 * clip volume. That is not a compromise. A 15 cm tire mark at 100 m is under two pixels;
 * everything R1 was doing out there was manufacturing an artefact to draw something that
 * should not have been drawn at all.
 *
 * ---------------------------------------------------------------------------
 * How the rut reads
 * ---------------------------------------------------------------------------
 *
 * Blending is MULTIPLY, not alpha-over-a-dark-colour. A track is not paint: it is the
 * same ground, compacted, shaded and displaced. Multiplying preserves whatever value
 * structure and lighting the terrain shader produced underneath and simply takes it
 * down, so the track still gets darker in shadow and lighter in the sun without knowing
 * anything about either.
 *
 * On top of that the cross-section is *lit*, not just tinted. Each fragment rebuilds the
 * rut's own surface normal by tilting the ground normal along the lateral direction by
 * the analytic slope of the rut profile, lights it with the real sun, and multiplies by
 * the ratio against the flat ground's own lighting. That single term is what makes the
 * mark read as a groove with a wall the sun catches and a wall it does not — form and
 * light doing the work, which is the whole stylized-grounded direction. It costs one dot
 * product and it is the difference between a rut and a brown stripe.
 *
 * ---------------------------------------------------------------------------
 * Memory
 * ---------------------------------------------------------------------------
 *
 * Bounded by construction, not by a cleanup pass. TRACK_SEGMENTS quads are allocated
 * once at module load and written round a ring; a twenty-minute session writes the same
 * ~960 KB of typed array over and over. Nothing in the per-frame path allocates, and the
 * per-frame upload is only the two or three quads that were actually written.
 *
 * ---------------------------------------------------------------------------
 * R5 — the round where the mark first reached the screen
 * ---------------------------------------------------------------------------
 *
 * Every round up to here tuned contrast. Contrast was never the problem. Measured with a
 * probe that projects the written vertices through the live camera matrices
 * (`captures/w3-vfx-r2-tools/diag.mjs`), on `descend` at 52 km/h with the rear wheel in
 * contact:
 *
 *     nearest written vertex   2.08 m from the lens
 *     of the 40 nearest,       12 inside the clip volume
 *     those 12 project to      py 830-2947 of a 900 px frame
 *
 * — that is, essentially the whole ribbon is *below or behind the lens*. Rendering it
 * with the depth test off and an opaque material (frames `_B_opaque` / `_C_nodepth` in
 * `captures/w3-vfx-r2-diag`) put a single 130 x 40 px sliver on screen, at the rear tyre,
 * and nothing else anywhere. So the geometry was reaching the rasteriser, it was landing
 * where no camera could see it, and no amount of tint was ever going to change that.
 *
 * A chase camera sits 2.1-5.2 m behind the bike and 1.5-1.6 m over the deck. The only
 * ground it can see that the rider has already ridden is the wedge **between the lens and
 * the bike** — two or three metres of it, in the bottom third of the frame — plus whatever
 * swings out to the side in a corner. That wedge is the entire budget. Two things were
 * spending it:
 *
 *  1. **The ribbon stopped up to 0.6 m short of the tyre, always.** A quad is only
 *     committed once the wheel has moved MIN_STEP, so the newest cross-section trails the
 *     contact patch by most of a segment — and the contact patch is at the *far* end of
 *     the visible wedge. The part of the mark the camera can see best was the part that
 *     had not been drawn yet. Fixed by TIP_SEGMENTS below: two quads, one per wheel, that
 *     are rewritten from the pen's last committed edge to the live contact patch every
 *     frame. They cost eight vertices of upload and they close the gap exactly.
 *
 *  2. **Every chatter skip broke the ribbon and cost another 0.3 m on landing.**
 *     `contact` is `travel > 0` — a knife edge, far twitchier than a real casing with a
 *     6 cm patch that conforms. On this terrain at 50 km/h the flag flickers constantly,
 *     and each flicker lifted the pen, restarted it on touchdown *without emitting*, and
 *     then waited another MIN_STEP. The mark became more gap than mark. AIR_BRIDGE keeps
 *     the pen down across losses shorter than 0.35 m of ground — 23 ms at 15 m/s, a skip
 *     rather than a jump — and still breaks it hard for anything longer, so a double still
 *     shows two marks and a gap.
 *
 *  3. **A short touchdown committed nothing at all.** A quad needs MIN_STEP of travel, so
 *     a wheel that kissed the ground for 0.2 m between two hops emitted exactly zero of
 *     them: the live tip showed the mark while the tyre was down and it vanished the
 *     instant the tyre left, because there was never anything permanent behind it.
 *     `flushPen` banks the tip as a real quad on every pen-up, so every contact leaves
 *     something.
 *
 * Contrast was re-tuned *after* all three, in that order, and it went into RELIEF, the
 * hemispheric ambient in the fragment shader and the lip — form and light — rather than
 * into TRACK_TINT, which had already been pushed twice by rounds that were looking at the
 * wrong thing.
 *
 * ---------------------------------------------------------------------------
 * R6 — the mark is now LIT, and this file finally uses the shared library
 * ---------------------------------------------------------------------------
 *
 * This was the one consumer in the game that never adopted `src/render/shading/relief.ts`.
 * Everything above it was hand-rolled: its own `uSunDir`, its own invented hemispheric sky
 * (`0.14 + 0.38 * max(n.y, 0)`), its own slope expression, its own occlusion constant. So
 * whatever it put on screen was a shape the rest of the scene's light had never heard of,
 * and the honest description of it was a tint with a gradient in it.
 *
 * Three things changed, and they are one change.
 *
 *  1. **There is a height field now, in metres.** A groove 2.4 cm deep, a lip 0.9 cm
 *     proud, knob blocks pressed 1 cm into the floor, and a grain over all of it. It is
 *     handed to `rlfBumpH`, which recovers the surface frame from the screen-space
 *     derivatives of the world position and returns the perturbed normal — Mikkelsen's
 *     surface-gradient form, with the library's 4:1 slope cap. R5 wrote an analytic
 *     *slope* instead, so the only thing that could ever have form was the single profile
 *     that one expression described: no tread, no crumble, no broken edge, and any new
 *     term had to be re-differentiated by hand. Now anything added to `h` becomes form.
 *
 *  2. **Both normals are lit by the scene's own light.** `rlfEnv()` for the sun's
 *     direction and radiance, the sky and the ground bounce — the same `Float32Array`
 *     every other consumer of the library holds — and `gripTerrainLight()` from sky.ts for
 *     how much of that sun and sky actually reach this square metre, which is the ridge
 *     shadow, the canopy shadow and the rider ellipsoid. `rlfCrease` supplies the groove's
 *     own sky occlusion, analytically and independently of any light, which is the term
 *     that still says "rut" under a closed canopy.
 *
 *  3. **The albedo went from a 68% cut to a 24% one.** R5's tint table was pushed twice by
 *     rounds trying to make an invisible mark visible, and a 68% albedo cut is not dirt,
 *     it is paint — the exact thing six critics called this game out for. What a tyre does
 *     to albedo is compact it slightly; everything else it does is geometry. That is now
 *     where the contrast comes from.
 *
 * The blend is still MULTIPLY, and that is not a compromise — it is the mechanism by which
 * absolute radiance reaches the screen without a G-buffer. The fragment emits
 * `albedoRatio * L(n) / L(gn)`; the destination it multiplies is `albedo_ground * L(gn)`;
 * what lands is `albedo_ground * albedoRatio * L(n)`, the mark's own radiance under the
 * scene's own light. Doing it this way rather than by writing radiance directly is what
 * lets the mark keep the ground's per-fragment albedo — its grain, its cover patches, its
 * wetness, its aerial perspective — instead of pasting a flat colour over all of it, and
 * it is why nothing here has to know about three's shadow map.
 *
 * ### The instrument, because the numbers above R5 were measured with a broken one
 *
 * R2's visibility probe diffed two `page.screenshot()` calls and reported 41% of the frame
 * changed — with an A-vs-A control of 41% of the frame. Its noise floor was its signal, so
 * every number it produced about this mark, in either direction, meant nothing.
 * `captures/w3-vfx-r3-tools/probe.mjs` toggles the mesh between two `renderer.render()`
 * calls inside ONE JS task and reads the drawing buffer back before the task yields. Its
 * control is 0 pixels. Measured on it, through the shipping post chain, riding a built
 * line at chase range: peak-to-peak across the mark is 0.76-0.92 of the value of the
 * ground it is cut into, and the lip is brighter than that ground rather than only darker.
 */

import * as THREE from 'three';
import { registerSystem, type SystemContext } from '../core/systems';
import { Surface } from '../world/terrain';
import { Weather } from '../world/weather';
import { activeSky } from '../render/sky';
import {
  reliefGLSL, reliefUniforms, installReliefSystem,
} from '../render/shading/relief';
import { Tuning } from '../core/tuning';
import type { WheelState } from '../bike/physics';
import { clamp01, lerp } from '../core/math';
import { declareInvariant } from '../core/audit';

const G = 9.81;

// ---------------------------------------------------------------------------
// Bound and spacing
// ---------------------------------------------------------------------------

/**
 * Ring capacity, in quads. 16 000 vertices, which keeps the index buffer in Uint16 and
 * the whole system inside ~960 KB that is never reallocated. At the emission spacing
 * below that is 1.2-2.4 km of riding per wheel-pair before the oldest quad is recycled.
 */
export const TRACK_SEGMENTS = 4000;

/**
 * Quads reserved at the head of the buffer for the two live tips — see the R5 note in the
 * header. Slot 0 is the rear wheel's, slot 1 the front's. They are *not* part of the ring:
 * the ring runs over [RING_START, TRACK_SEGMENTS) and the tips are rewritten in place
 * every frame, which is what lets the mark reach the contact patch instead of stopping
 * most of a segment short of it.
 *
 * Putting them at the head rather than the tail is what keeps the draw range one
 * contiguous span from index zero, so this costs no extra draw call and no extra state.
 */
const TIP_SEGMENTS = 2;
const RING_START = TIP_SEGMENTS;
const RING_CAP = TRACK_SEGMENTS - RING_START;

/** Shortest segment. Below this a slow, tight turn spends the ring on nothing. */
const MIN_STEP = 0.30;
/** Longest segment. A quad much longer than this stops following sub-metre ground. */
const MAX_STEP = 0.60;
/** Heading change that forces an early segment, rad. Keeps berms from being chorded. */
const TURN_STEP = 0.05;

/**
 * How much ground a wheel may cross with `contact` false before the ribbon breaks, m.
 *
 * Not a time — a distance, because what decides whether a tyre left a continuous mark is
 * how far it skipped, not for how long. 0.35 m is 23 ms at 15 m/s: chatter, a root, the
 * back of a roller. Anything longer is a jump and the mark should stop, which is what
 * makes a gap appear across a double instead of a stripe painted through the sky.
 */
const AIR_BRIDGE = 0.35;

/**
 * Half-width of the *rut* a rolling tire cuts, m.
 *
 * ---------------------------------------------------------------------------
 * R4 — why this went up, and why that is not stylizing proportion away
 * ---------------------------------------------------------------------------
 *
 * Round 1 came back with "tire tracks are invisible from the camera the game is
 * actually played from", and the previous round answered it by arguing the number was
 * honest rather than by measuring what reached the screen. Measured, in
 * `corner_13p5s.png` at chase range: the ribbon *is* drawn, and the part of it at full
 * strength was **48 mm across** — two or three pixels — because `floorT` fell away from
 * the centreline at t = 0.42 of a 56% rut fraction, so 11 cm of "rut" was really 5 cm of
 * mark and 6 cm of ramp at a third of the strength. The band the eye could find was the
 * ramp, and a ramp with no plateau anywhere in it has no width.
 *
 * So two things changed and neither of them is a 40 cm stripe:
 *
 *  - The rut is 15 cm rather than 11. A 2.4-2.6" casing is 60-66 mm across, but the
 *    ground it presses does not fail at the edge of the casing: the shoulder knobs
 *    crumble the walls outward and the displaced soil stands up beyond that. 15 cm of
 *    *disturbed* ground under a loaded gravity tire is the honest figure, and it is the
 *    figure a rut in a photograph measures.
 *  - The plateau in the cross-section now holds most of that width (see `floorT` in the
 *    fragment shader) instead of a fifth of it. That is the change that matters and it
 *    costs no width at all — it is the difference between drawing a rut and drawing the
 *    blur at the edge of one.
 */
const RUT_HALF = 0.085;
/**
 * Extra rut half-width at full slip: a sliding tire smears a much wider band.
 *
 * Weighted by slip squared, so it only arrives at a genuine drift. At full slide the
 * disturbed band is 60 cm, which is what a rear wheel stepped fully out actually leaves
 * on loose ground — that smear is the most visible mark a bike makes and it is the one
 * a rider goes looking for after a corner.
 */
const RUT_HALF_SLIP = 0.075;
/**
 * Fraction of the quad's half-width the rut itself occupies. The rest is the displaced
 * lip either side — the pushed-up soil that is the other half of "cut into the ground".
 * Must match RUT_FRAC in the fragment shader.
 */
const RUT_FRAC = 0.50;

/**
 * Where the ribbon stops, m.
 *
 * See the header. Past TRACK_FADE1 a tire mark is sub-two-pixels and every technique for
 * keeping it on a surface the clipmap is no longer resolving costs more than it buys.
 * The cull margin is larger than one quad so that every vertex of a culled quad is
 * already at zero alpha — a partially culled triangle would otherwise be stretched
 * across the frame.
 */
const TRACK_FADE0 = 34;
const TRACK_FADE1 = 78;
const TRACK_CULL = TRACK_FADE1 + 3;

/**
 * Depth bias. All three terms are constants; none scales with view distance.
 *
 * LIFT is sized against the clipmap's own reconstruction error: the innermost ring
 * samples at 0.5 m and draws the cell as two flat triangles, so the drawn surface sits
 * slightly above or below the analytic height the contact patch reported. That error was
 * measured rather than guessed — 500 probes over a 40 m square around the rider,
 * comparing the terrain function against the triangulated 0.5 m grid — and it came out at
 * 2.4 mm at the 99th percentile and 3.1 mm at the worst. So the near field never needed
 * centimetres of anything, which is the other half of why R1's distance-scaled bias was
 * the wrong tool: it was solving a problem that does not exist here. 22 mm is an order of
 * magnitude over the measured error, still under the depth of the rut being drawn, and
 * comfortably inside the 80 mm the trail tread ribbon already lifts by.
 */
const TRACK_LIFT = 0.035;
const TRACK_PUSH = 0.030;

/**
 * How readily each surface takes a track, 0..1.
 *
 * A different axis from the dust coefficient, so it needs its own table. Loam throws
 * almost no dust and takes the deepest rut on the mountain; loose throws the most dust
 * and the berm it makes half fills itself back in. Rock takes nothing — a tire mark on
 * slab is a scuff you cannot see from a bike.
 */
const TRACK_TAKE = [0.72, 1.0, 0.92, 0.10, 0.42, 0.66, 1.0];

/**
 * How much a fresh track changes the ground's ALBEDO — not what colour it is.
 *
 * ---------------------------------------------------------------------------
 * R6 — why these went from 0.32 to 0.80
 * ---------------------------------------------------------------------------
 *
 * R5 read these as "the colour of a tire mark" and pushed them to a 68% cut, twice, in
 * two separate rounds that were each trying to make an invisible mark visible. A 68% cut
 * is not dirt; it is paint, and painting the ground darker is precisely what six critics
 * across six pieces called this whole game out for.
 *
 * What a tyre actually does to the albedo is modest. It compacts the surface — a
 * compacted soil scatters maybe 15-25% less than the loose crust the sun bleached beside
 * it — and it turns some of it over, which at the lip is *lighter* rather than darker.
 * Everything else a rut does to the image is geometry: a groove, a wall, a lip, tread
 * pressed into the floor, and the sky those shapes can and cannot see. That is now where
 * the contrast comes from (see the fragment shader), so the albedo can be honest.
 *
 * These multiply the ground's own albedo, so they carry its grain, its cover patches and
 * its wetness through unchanged instead of pasting a flat colour over all of it.
 */
const TRACK_TINT: readonly (readonly [number, number, number])[] = [
  [0.76, 0.73, 0.70], // hardpack — compacted, slightly warmer than the bleached crust
  [0.68, 0.65, 0.63], // loam — dark and damp under the duff
  [0.80, 0.76, 0.71], // loose — turns over pale
  [0.97, 0.97, 0.98], // rock — barely marks at all
  [0.74, 0.71, 0.68], // root
  [0.72, 0.70, 0.64], // grass — crushed rather than cut
  [0.90, 0.92, 0.97], // snow — compacted snow is barely darker and reads bluer
];
/** The same soil wet. Water darkens dirt further and takes the red out of it. */
const TRACK_TINT_WET: readonly (readonly [number, number, number])[] = [
  [0.70, 0.69, 0.69],
  [0.62, 0.61, 0.61],
  [0.74, 0.72, 0.71],
  [0.93, 0.94, 0.95],
  [0.68, 0.67, 0.67],
  [0.67, 0.67, 0.64],
  [0.87, 0.90, 0.96],
];

/**
 * Knob spacing along the track, as an angular frequency in rad/m. 40 puts a knob row
 * every 157 mm, which is about right for a gravity casing and is still four or five
 * pixels at the distance the LOD fade kills it.
 *
 * `arc` has to be wrapped or this pattern dies: it is a running arc length in metres, it
 * is stored in a Float32Array, and twenty minutes of riding puts it past 10 km — at which
 * point `sin(KNOB_FREQ * arc)` has no significant fractional bits left and the tread
 * pattern degenerates into banding. Wrapping at an exact whole number of knob periods
 * keeps the phase continuous across the wrap, so nothing is visible where it happens.
 */
const KNOB_FREQ = 40;
const ARC_WRAP = (Math.PI * 2 * 100) / KNOB_FREQ;

/** Strength of the compacted-surface highlight. Small on purpose; it is a cue, not a gloss. */
const SHEEN_BASE = 0.055;

/**
 * The rut, in metres. These replace R5's dimensionless `RELIEF` slope.
 *
 * A number in metres is checkable against a photograph and against the tyre: a loaded
 * 2.4-2.6" gravity casing on hardpack leaves 2-3 cm of depression across 15 cm, and the
 * soil the wall pushes out stands about half that proud. A dimensionless slope was not
 * checkable against anything, which is how it ended up being tuned twice by rounds that
 * were looking at the wrong problem.
 *
 * They are metres for a second reason too: `rlfBumpH` takes its scalar in metres and
 * recovers the surface gradient from the world-space derivatives, so the wall angle is
 * whatever 2.5 cm over the width of the transition really is — at any view distance,
 * on any quad width, with no re-derivation.
 *
 * ---------------------------------------------------------------------------
 * W4 R3 — 2.4 -> 3.1 cm, and the lip with it, because of where the camera now is
 * ---------------------------------------------------------------------------
 *
 * "Tire tracks are invisible from the camera the game is actually played from" has been
 * open for three rounds, and this round finally measured the camera rather than the mark.
 * The chase lens sits **2.4-2.6 m** behind the chassis and the rear contact patch projects
 * to **y = 828-947 on a 900 px frame** — at or below the bottom edge. So the only ridden
 * ground in shot is the wedge between the lens and the tyre plus whatever swings out in a
 * turn, and measured with the mesh-toggle A/B (control 0 px, `captures/w4-vfx-r3-trk/`)
 * that wedge is **0.6-2.6 % of the frame**, all of it below y = 616.
 *
 * A mark that small has to carry more contrast per pixel or it is not there. The lever is
 * the one R6 argued for — geometry, not albedo — and 3.1 cm is still inside the 2-3 cm a
 * loaded gravity casing actually presses into hardpack, at the top of it rather than the
 * middle. RUT_OCC goes with it for the same reason: a deeper groove really does see less
 * sky, and that term is the one that still says "rut" under canopy where the sun has no
 * contrast left to give.
 */
const RUT_DEPTH = 0.031;
const LIP_HEIGHT = 0.012;
/**
 * How much of the sky the groove loses to its own wall, 0..1, through `rlfCrease`.
 *
 * This is the term that carries the mark in shade. It is light-independent — an analytic
 * half-plane form factor, not a shadow — so under a closed canopy, where the sun term has
 * no contrast left to give, the rut is still darker than the ground and the lip is still
 * standing out of it.
 */
const RUT_OCC = 0.74;

// ---------------------------------------------------------------------------
// Buffers — allocated once, never grown.
// ---------------------------------------------------------------------------

const VERTS = TRACK_SEGMENTS * 4;
const posArr = new Float32Array(VERTS * 3);
const colArr = new Float32Array(VERTS * 4);
/**
 * (signed lateral -1..1, arc length m, quad half-width m).
 *
 * The half-width is new in R6 and it is what lets the fragment shader work in METRES
 * rather than in quad fractions: a groove is 2.5 cm deep whether the quad it is drawn on
 * is 30 cm wide (rolling) or 60 (a full slide), and every occlusion and slope term in the
 * new shader is a real length.
 *
 * The unit lateral vector R5 uploaded is gone with it. `rlfBumpH` recovers the surface
 * frame from the screen-space derivatives of the world position, so the cross-section
 * direction no longer has to be shipped per vertex — 12 bytes off every vertex and one
 * fewer attribute to keep in sync with the sweep.
 */
const uvArr = new Float32Array(VERTS * 3);
const nrmArr = new Float32Array(VERTS * 3);
const idxArr = new Uint16Array(TRACK_SEGMENTS * 6);

let cursor = RING_START;
let written = 0;
/** Whether either live tip has anything in it this frame — drives `mesh.visible`. */
let tipsLive = false;

/**
 * Segments written this frame. At 240 Hz and 19 m/s a wheel emits about one quad every
 * four sim steps, so this never holds more than a handful — and recording them
 * individually rather than as a lo..hi span is what stops the ring wrapping from
 * triggering a full 960 KB re-upload every kilometre.
 */
const dirtySeg = new Int32Array(24);
let dirtyN = 0;
let dirtyAll = false;

function markDirty(seg: number): void {
  if (dirtyN < dirtySeg.length) dirtySeg[dirtyN++] = seg;
  else dirtyAll = true;
}

// ---------------------------------------------------------------------------
// SELF-AUDIT (src/core/audit.ts).
//
// THIS FILE HAD NO INVARIANT ON THE BUS AT ALL, and its carried gap has been the same
// sentence for three rounds: "tire tracks are invisible from the camera the game is played
// from." A piece whose defect is "the thing is not on screen" and whose self-audit is empty
// is the ceiling-only problem in its purest form — there was nothing that could report it.
//
// R5's own header records how that happened and what the number has to be. Contrast was
// tuned for four rounds and contrast was never the defect: measured with a probe that
// projected the written vertices through the live camera, the whole ribbon was landing
// BELOW OR BEHIND THE LENS — "of the 40 nearest vertices, 12 inside the clip volume, and
// those 12 project to py 830-2947 of a 900 px frame". So the quantity to watch is not how
// dark the mark is. It is how much of the PICTURE it reaches.
// ---------------------------------------------------------------------------

/**
 * How many of the most recently committed quads are projected each frame.
 *
 * The visible wedge is what a chase camera can see of ground the rider has already
 * crossed: 2-5 m between the lens and the bike, plus whatever swings out in a corner. At
 * MIN_STEP..MAX_STEP that is under a dozen quads, and 48 covers up to 29 m of ribbon —
 * comfortably past anything the wedge can hold — for 200 projections a frame.
 */
const TRACK_SCAN_SEGS = 48;
/**
 * The floor, as a fraction of the frame's area.
 *
 * 0.0025 is a quarter of one percent of a 1600x900 frame, i.e. about 3600 px: a 1.4 m tread
 * seen at three metres is roughly 300 px wide, so this is a mark about a dozen pixels deep
 * on screen. That is deliberately not "a good tyre track" — it is the level below which
 * there is NOTHING, which is what the carried gap says is happening and what a floor is for.
 */
const TRACK_ONSCREEN_FLOOR = 0.0025;
/** Rolling speed above which a rider is entitled to a mark, m/s. */
const TRACK_PRESENCE_SPEED = 3.0;
/** ...held for this long, so the first frames after a landing are not read as an absence. */
const TRACK_PRESENCE_LAG = 0.5;

const INV_TRACK_SCREEN = declareInvariant({
  id: 'vfx.trackOnScreen',
  owner: 'vfx',
  what: 'share of the frame covered by tyre mark the rider has just laid down',
  unit: 'frac',
  budget: TRACK_ONSCREEN_FLOOR,
  compare: 'min',
  consequence: 'the rider cannot see where he has ridden — the mark is drawn below or behind the lens',
  source: 'src/vfx/tracks.ts TRACK_ONSCREEN_FLOOR',
});

/**
 * ...and the ceiling that keeps the floor honest.
 *
 * A floor alone is satisfied by a mark 9 m wide. This is the gap between the newest
 * committed cross-section and the contact patch it is supposed to reach — the defect
 * TIP_SEGMENTS exists to remove, and the one that puts the break exactly where the camera
 * looks best. It is measured rather than asserted because the tip is rewritten every frame
 * and can silently stop being written at all.
 */
const INV_TRACK_GAP = declareInvariant({
  id: 'vfx.trackTipGapM',
  owner: 'vfx',
  what: 'distance from the drawn end of the mark to the contact patch it is swept from',
  unit: 'm',
  budget: 0.35,
  consequence: 'the mark stops short of the tyre, and the gap is the part of it the chase camera sees best',
  source: 'src/vfx/tracks.ts TIP_SEGMENTS, MIN_STEP',
});

/** Held-rolling timer for the floor's gate, and last frame's measured coverage. */
let rollHeldS = 0;
let onScreenFrac = 0;
/** Scratch for one quad's projected corners. Allocated once; the per-frame path allocates nothing. */
const tmpNdcX = new Float64Array(4);
const tmpNdcY = new Float64Array(4);

// ---------------------------------------------------------------------------
// Pen state, one per wheel. Preallocated; nothing in the write path allocates.
// ---------------------------------------------------------------------------

interface Pen {
  down: boolean;
  /** Centre of the last emitted cross-section. */
  x: number; y: number; z: number;
  /** Its two edge vertices, reused verbatim as the next quad's leading edge so
   *  consecutive segments share an edge exactly and the ribbon has no cracks. */
  lx: number; ly: number; lz: number;
  rx: number; ry: number; rz: number;
  /** Ground normal, lateral, half-width and colour carried at that cross-section. */
  nx: number; ny: number; nz: number;
  ex: number; ey: number; ez: number;
  hw: number;
  cr: number; cg: number; cb: number; ca: number;
  /** Direction of the last segment, for the turn test. */
  dx: number; dz: number;
  /** Arc length, for the tread pattern's v coordinate. */
  arc: number;
  /** Ground crossed since `contact` went false, m. See AIR_BRIDGE. */
  airM: number;
  /** Where the wheel's patch was on the previous step, for measuring airM. */
  ax: number; az: number;
  hasA: boolean;
  /** Half-width and strength the tip should be drawn at — refreshed every sim step. */
  tipHw: number;
  tipStrength: number;
  /** Whether the wheel is on the ground right now, so the tip knows to draw. */
  onGround: boolean;
  /**
   * The last contact patch the wheel actually had, its ground normal and its tint.
   *
   * Kept separately from `cx/cy/cz` because those keep tracking the hillside under a wheel
   * that has left it: the tip has to end where the tyre last *touched*, and so does the
   * quad the flush banks on the way up.
   */
  tx: number; ty: number; tz: number;
  tnx: number; tny: number; tnz: number;
  tcr: number; tcg: number; tcb: number;
}

function makePen(): Pen {
  return {
    down: false,
    x: 0, y: 0, z: 0,
    lx: 0, ly: 0, lz: 0,
    rx: 0, ry: 0, rz: 0,
    nx: 0, ny: 1, nz: 0,
    ex: 0, ey: 0, ez: 1,
    hw: 0.15,
    cr: 1, cg: 1, cb: 1, ca: 0,
    dx: 1, dz: 0,
    arc: 0,
    airM: 0,
    ax: 0, az: 0, hasA: false,
    tipHw: 0.15, tipStrength: 0, onGround: false,
    tx: 0, ty: 0, tz: 0,
    tnx: 0, tny: 1, tnz: 0,
    tcr: 1, tcg: 1, tcb: 1,
  };
}

const penRear = makePen();
const penFront = makePen();

// ---------------------------------------------------------------------------

/**
 * Is `gripTerrainLight()` — sky.ts's analytic ridge / canopy / rider-ellipsoid sun
 * occlusion — reachable from a raw ShaderMaterial?
 *
 * sky.ts installs it by appending to `THREE.ShaderChunk.common` and publishing its three
 * uniforms into every entry of `THREE.ShaderLib`, which is how it reaches "every lit
 * material" without any of them importing anything. A ShaderMaterial resolves `#include`
 * the same way a built-in one does, so `#include <common>` picks the function up; the
 * uniforms have to be taken off ShaderLib by hand because a ShaderMaterial does not
 * inherit them. Both halves are checked here rather than assumed — sky.ts skips the whole
 * installation under `?noAerial`, and a shader that calls an undefined function does not
 * render at all.
 *
 * This matters because it is the difference between the mark being lit and the mark being
 * *tinted*. See the R6 note in the header.
 */
function skyShadowUniforms(): Record<string, THREE.IUniform> | null {
  const chunks = THREE.ShaderChunk as unknown as Record<string, string>;
  if (!chunks.common || chunks.common.indexOf('gripTerrainLight') < 0) return null;
  const lib = THREE.ShaderLib as unknown as
    Record<string, { uniforms?: Record<string, THREE.IUniform> }>;
  const src = lib.basic && lib.basic.uniforms ? lib.basic.uniforms : null;
  if (!src) return null;
  const out: Record<string, THREE.IUniform> = {};
  for (const k of ['uGripAerial', 'uGripSunOcc', 'uGripSunOccFine']) {
    if (!src[k]) return null;
    out[k] = src[k];
  }
  return out;
}

/** GLSL float literal. */
const gf = (v: number): string => (Number.isInteger(v) ? v.toFixed(1) : String(v));

function trackVert(sky: boolean): string {
  return /* glsl */ `
${reliefGLSL('vertex', false)}
${sky ? '#include <common>' : ''}

attribute vec4 aCol;
attribute vec3 aUV;    // (signed lateral -1..1, arc length m, quad half-width m)
attribute vec3 aNrm;

uniform float uLift;
uniform float uPush;
uniform vec2  uFade;   // (start, end) of the distance fade, m
uniform float uCull;   // hard cull distance, m

varying vec4 vCol;
varying vec3 vUV;
varying vec3 vNrm;
varying vec3 vWP;
varying float vFar;

void main() {
  // The mesh matrix is identity; these are world-space positions.
  // Lift along the ground normal, not along +Y: on a 30 degree sidehill a vertical lift
  // slides the mark downhill across the surface it is supposed to be cut into.
  vec3 wp = position + normalize( aNrm ) * uLift;

  vec3 toEye = cameraPosition - wp;
  float d = max( length( toEye ), 1e-3 );
  // A fixed nudge along the view ray. Exact in screen position — a point moved along its
  // own view ray projects to the same pixel — and it is the only bias that buys anything
  // when you are looking straight down at the ground.
  wp += ( toEye / d ) * uPush;

  vFar = 1.0 - smoothstep( uFade.x, uFade.y, d );

  if ( d > uCull ) {
    // Outside the clip volume: the triangle is discarded before rasterisation. Every
    // vertex of a quad this far out is already at vFar = 0, so nothing that could still
    // have contributed is ever thrown away by a partially culled triangle.
    gl_Position = vec4( 2.0, 2.0, 2.0, 1.0 );
    return;
  }

  vCol = aCol;
  vUV = aUV;
  vNrm = aNrm;
  vWP = wp;
${sky ? '  vGripWP = wp;   // the world position gripTerrainLight() samples on' : ''}
  gl_Position = projectionMatrix * modelViewMatrix * vec4( wp, 1.0 );
}
`;
}

function trackFrag(sky: boolean): string {
  return /* glsl */ `
${reliefGLSL('fragment', false)}
${sky ? '#include <common>' : `
// sky.ts's aerial/shadow installation was skipped (?noAerial). Everything below still
// works; the mark simply cannot know about ridge and canopy shadow, which is the same
// thing that is true of every other material in that mode.
vec2 gripTerrainLight( vec3 wp ) { return vec2( 1.0 ); }
`}

uniform float uSheen;
uniform float uDepthM;   // groove depth at full strength, metres
uniform float uLipM;     // displaced lip height at full strength, metres
uniform float uOcc;      // strength of the groove's own sky occlusion, 0..1
uniform float uKnob;

varying vec4 vCol;
varying vec3 vUV;
varying vec3 vNrm;
varying vec3 vWP;
varying float vFar;

// Fraction of the quad the rut itself occupies; outside it is displaced soil.
const float RUT_FRAC = 0.50;
// Wrap of the diffuse terminator. 0.30 is the low end of what a dry mineral soil shows;
// it is deliberately modest, because wrapping hard is how a surface stops having a
// terminator at all.
#define RLF_TRK_WRAP 0.30
#define RLF_TRK_WRAPPED( c ) ( max( ( c ) + RLF_TRK_WRAP, 0.0 ) / ( 1.0 + RLF_TRK_WRAP ) )

void main() {
  float far = vFar;
  if ( far <= 0.002 ) discard;

  // Everything below is in METRES. That is the whole reason the half-width is now a
  // vertex attribute: a rut is 2.5 cm deep whether the quad carrying it is 30 cm across
  // (rolling) or 60 (a rear wheel fully stepped out), and a profile expressed in
  // fractions of the quad silently made the smear four times deeper than the rut.
  float hw = max( vUV.z, 1e-3 );
  float au = abs( vUV.x );
  float lat = au * hw;              // metres from the centreline
  float rut = RUT_FRAC * hw;        // where the groove wall stands
  float t = au / RUT_FRAC;
  float str = clamp( vCol.a * far, 0.0, 1.0 );

  vec3 gn = normalize( vNrm );
  RlfLod L = rlfLodAt( vWP );
  RlfEnv E = rlfEnv();
  vec2 puv = rlfPlaneUV( vWP, gn );

  // --- the disturbed band -------------------------------------------------
  // Its outer edge is DISPLACED BY A FIELD before it is crossed, exactly as
  // rlfCoverBlend displaces a cover margin. A parametric edge on a swept quad is a
  // straight line in world space and the eye reads straight lines as geometry — which is
  // most of why the previous mark read as a painted stripe with a ruled edge rather than
  // as ground a tyre had torn. Faded at the break's own Nyquist limit so a distant mark
  // simplifies instead of dissolving into speckle.
  float eLod = rlfBandLod( L, 2.4 );
  float brk = ( rlfFbm( puv * 2.4, 2 ) - 0.5 ) * 0.30 * eLod;
  float band = 1.0 - smoothstep( 0.76 + brk, 0.98 + brk, au );
  if ( band < 0.004 ) discard;

  // Flat-bottomed groove, then the wall, then the soil the wall pushed out. A tyre's
  // contact patch is flat across almost its whole width and only fails at the shoulder,
  // so the plateau is most of the rut rather than a fifth of it.
  //
  // The wall runs 0.55 -> 1.05 rather than 0.60 -> 1.00, which at 2 cm of depth over
  // 15 cm of rut is a 34 degree flank. The first pass of R6 had it at 40 and, with this
  // game's 7.9 am sun, a 40 degree flank facing away from it is genuinely black — the
  // shading was right and the ANGLE was wrong, and what landed on screen was a ditch
  // with a sunlit berm rather than a tyre mark.
  float floorT = ( 1.0 - smoothstep( 0.55, 1.05, t ) ) * band;
  // Broken along its length, because a continuous ridge of pushed-up soil is a rope and
  // reads as one. Soil fails in chunks: some of the lip stands up, some of it crumbled
  // straight back into the rut behind the tyre.
  float lipBrk = mix( 1.0, 0.35 + 1.15 * rlfFbm( puv * 3.6 + 21.7, 2 ), rlfBandLod( L, 3.6 ) );
  float lip = smoothstep( 0.96, 1.24, t ) * ( 1.0 - smoothstep( 1.24, 1.86, t ) ) * band * lipBrk;
  // --- the height field, in metres ----------------------------------------
  //
  // This is the object the rest of the shader is derived from, and it is the whole
  // difference from R5. R5 wrote an analytic *slope* and tipped the normal by it, so the
  // only thing that could have form was the one profile that expression described: no
  // tread, no crumble, no broken edge, and every term had to be re-derived by hand if the
  // profile changed. rlfBumpH takes a scalar in metres and recovers the surface gradient
  // from the screen-space derivatives of the world position, so ANY of these terms —
  // including ones added later — becomes form the sun can find, for free.
  //
  //   groove   2.5 cm down across the plateau
  //   lip      1.2 cm proud, the soil the wall pushed out
  //   tread    knob rows pressed into the floor, faded at their own Nyquist limit
  //   grain    the disturbed surface is broken, not smooth
  float treadLod = rlfBandLod( L, ${gf(KNOB_FREQ / (Math.PI * 2))} );
  // Flat-topped knobs with a gap between them, not a sine: a tyre presses BLOCKS into
  // the ground and the shape of the block is what a rider recognises. Chevroned by the
  // |u| term, which skews the phase across the tread.
  float tread = smoothstep( -0.35, 0.45, sin( vUV.y * uKnob + au * 2.6 ) );
  float grainLod = rlfBandLod( L, 13.0 );
  float grain = rlfFbm( puv * 13.0, 2 ) - 0.5;

  float h = str * (
      - uDepthM * floorT
      + uLipM * lip
      - uDepthM * 0.46 * floorT * tread * treadLod
      + uDepthM * 0.45 * band * grain * grainLod
    );

  // THE multiplier. Passing rlfReliefAmp means this mark fades with distance, with
  // camera speed and at a grazing angle on exactly the same schedule as the terrain
  // detail, the cover and the scatter — which is what stops the world reading as several
  // games at several distances. The frequency is the cross-section's own: one cycle per
  // ribbon width.
  float amp = rlfReliefAmp( L, 0.5 / hw );
  vec3 n = rlfBumpH( gn, vWP, h, amp );

  // --- light --------------------------------------------------------------
  //
  // The mark is lit, not tinted. Both normals are put through the SAME environment the
  // ground under them is lit by: relief.ts's shared block for the sun radiance, the sky
  // and the ground bounce, and sky.ts's own analytic occlusion — ridge shadow, canopy
  // shadow and the rider ellipsoid — for how much of the sun and the sky actually reach
  // this square metre of hillside.
  //
  // What reaches the screen is albedoRatio * L(n) / L(gn), and the destination it
  // multiplies is albedo_ground * L(gn). So the pixel that lands is
  //
  //     albedo_ground * albedoRatio * L(n)
  //
  // — the mark's own absolute radiance, computed from the scene's own light. The
  // hardware multiply is the mechanism, not the model: it is what lets the mark keep the
  // ground's per-fragment albedo (its grain, its cover patches, its wetness) instead of
  // pasting a flat colour over all of it, and it is why nothing here has to know about
  // three's shadow map. The one term it also scales is the aerial in-scatter already
  // summed into the destination, which over the 34-78 m the ribbon lives in is a fraction
  // of a percent.
  vec2 tl = gripTerrainLight( vWP );
  float sun = E.sunVis * tl.x;
  vec3 S = E.sunW;

  // A rut sees a slot of sky where the ground beside it sees all of it. rlfCrease is the
  // analytic ambient occlusion of a half-plane of that height, it is light-INDEPENDENT,
  // and it is the term that still says "rut" under a closed canopy or a flat overcast
  // where every normal-based term has no contrast to work with. The lip stands proud of
  // all of it and gets the sky back.
  float occ = rlfCrease( lat - rut, max( uDepthM * str, 1e-4 ), uOcc * str );
  occ = mix( occ, 1.0, lip );

  vec3 ambG = mix( E.gnd, E.sky, 0.5 + 0.5 * gn.y ) * tl.y;
  vec3 ambT = mix( E.gnd, E.sky, 0.5 + 0.5 * n.y ) * tl.y * occ;

  // Wrapped Lambert, and the wrap is the difference between a rut and a slot cut in the
  // deck. Dirt is not a smooth dielectric: light entering a rough soil scatters under the
  // surface and comes back out of facets the sun never reached, and the walls of a
  // centimetre-scale groove bounce into each other besides. A hard max(dot(N,S),0) on a
  // perturbed micro-normal ignores both and drives the away-facing flank to exactly zero
  // direct light, which under this game's low morning sun measured as an 0.12x multiplier
  // — black. RLF_WRAP is applied to BOTH normals, so flat ground is still exactly 1.0 and
  // the identity the whole ratio depends on is untouched.
  vec3 lit0 = E.key * sun * RLF_TRK_WRAPPED( dot( gn, S ) ) + ambG;
  vec3 lit1 = E.key * sun * RLF_TRK_WRAPPED( dot( n, S ) ) + ambT;
  // Still wide — a wall turned out of a raking sun really does lose most of its light and
  // the wall opposite really does gain half again. R5 clamped this to [0.50, 1.70] and
  // then lerped it back toward 1 by the mark strength, which between them threw away most
  // of the shape before it was drawn. The floor is here so a single fragment of a
  // mis-scaled field cannot punch a black hole in the hillside, not as a look.
  vec3 ratio = clamp( lit1 / max( lit0, vec3( 1e-4 ) ), vec3( 0.26 ), vec3( 2.30 ) );

  // --- albedo -------------------------------------------------------------
  // Only how much the tyre CHANGED the ground, not what the ground is. Compacted soil
  // scatters a little less than the crust the sun bleached; the soil standing proud at
  // the lip is freshly turned and scatters a little more. Both are modest, and they are
  // modest on purpose — R5 cut the albedo by 68% and that is a paint job, which is
  // exactly what six critics called this file.
  vec3 alb = mix( vec3( 1.0 ), vCol.rgb, clamp( band * str, 0.0, 1.0 ) );
  alb *= 1.0 + lip * str * 0.10;

  vec3 mul = alb * ratio;

  // Compacted dirt is smoother than the ground beside it and holds a highlight loose
  // grains scatter away. Added rather than multiplied — it is the one part of "darker AND
  // smoother" a darkening cannot express — and gated on the sun actually reaching here,
  // so a track under a tree does not glint.
  vec3 hv = normalize( S + gn );
  mul += uSheen * pow( max( dot( n, hv ), 0.0 ), 30.0 ) * floorT * str * sun;

  gl_FragColor = vec4( mul, 1.0 );
}
`;
}

let mesh: THREE.Mesh | null = null;
let geo: THREE.BufferGeometry | null = null;
/**
 * Whether the mark got sky.ts's analytic sun occlusion, recorded so a capture can ASSERT
 * it rather than assume it. A silently-missing shadow term does not throw, does not warn
 * and does not look obviously wrong in one frame — it just quietly lights every rut on
 * the mountain as if it were in the open.
 */
let skyWired = false;
let mat: THREE.ShaderMaterial | null = null;
let attrPos: THREE.BufferAttribute | null = null;
let attrCol: THREE.BufferAttribute | null = null;
let attrUV: THREE.BufferAttribute | null = null;
let attrNrm: THREE.BufferAttribute | null = null;

function build(): void {
  for (let s = 0; s < TRACK_SEGMENTS; s++) {
    const v = s * 4;
    const i = s * 6;
    idxArr[i] = v; idxArr[i + 1] = v + 1; idxArr[i + 2] = v + 2;
    idxArr[i + 3] = v; idxArr[i + 4] = v + 2; idxArr[i + 5] = v + 3;
  }

  geo = new THREE.BufferGeometry();
  attrPos = new THREE.BufferAttribute(posArr, 3);
  attrCol = new THREE.BufferAttribute(colArr, 4);
  attrUV = new THREE.BufferAttribute(uvArr, 3);
  attrNrm = new THREE.BufferAttribute(nrmArr, 3);
  attrPos.setUsage(THREE.DynamicDrawUsage);
  attrCol.setUsage(THREE.DynamicDrawUsage);
  attrUV.setUsage(THREE.DynamicDrawUsage);
  attrNrm.setUsage(THREE.DynamicDrawUsage);
  geo.setAttribute('position', attrPos);
  geo.setAttribute('aCol', attrCol);
  geo.setAttribute('aUV', attrUV);
  geo.setAttribute('aNrm', attrNrm);
  geo.setIndex(new THREE.BufferAttribute(idxArr, 1));
  geo.setDrawRange(0, 0);
  // The reserved tips start collapsed rather than zeroed: a zeroed vertex carries a zero
  // normal, and the vertex shader takes `normalize(aNrm)` unconditionally.
  collapseQuad(0);
  collapseQuad(1);
  // Never culled: the ring covers a kilometre of mountain and its bounding sphere would
  // have to be recomputed every time the cursor moves. The vertex shader's own distance
  // cull is what keeps the off-screen part of it free.
  geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);

  // Both are read once, here: the shader source has to be built knowing whether the
  // analytic terrain shadow is installed, and the uniforms have to point at the same
  // objects every other material in the scene points at.
  const skyU = skyShadowUniforms();
  skyWired = skyU !== null;

  mat = new THREE.ShaderMaterial({
    vertexShader: trackVert(skyU !== null),
    fragmentShader: trackFrag(skyU !== null),
    uniforms: {
      // The shared light block: the same IUniform — and the same Float32Array — every
      // other consumer of relief.ts holds, so the sun this rut is lit by is the sun the
      // hillside it is cut into is lit by, by construction rather than by two systems
      // agreeing to copy the same number.
      ...reliefUniforms(),
      ...(skyU ?? {}),
      uSheen: { value: SHEEN_BASE },
      uDepthM: { value: RUT_DEPTH },
      uLipM: { value: LIP_HEIGHT },
      uOcc: { value: RUT_OCC },
      uKnob: { value: KNOB_FREQ },
      uLift: { value: TRACK_LIFT },
      uPush: { value: TRACK_PUSH },
      uFade: { value: new THREE.Vector2(TRACK_FADE0, TRACK_FADE1) },
      uCull: { value: TRACK_CULL },
    },
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: THREE.MultiplyBlending,
    side: THREE.DoubleSide,
    // The real depth bias. Coplanar geometry is exactly what polygon offset exists for,
    // and it works in the units the depth test actually compares in — which no amount of
    // shoving the vertices around in world space can do reliably at every view angle.
    polygonOffset: true,
    polygonOffsetFactor: -4,
    polygonOffsetUnits: -8,
    // The output is a multiplier applied to scene radiance, not a radiance itself.
    toneMapped: false,
  });

  mesh = new THREE.Mesh(geo, mat);
  mesh.name = 'vfx-tracks';
  mesh.frustumCulled = false;
  mesh.matrixAutoUpdate = false;
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  // Above the trail tread ribbon (renderOrder 4, transparent, depthWrite false), below
  // the airborne dust (12). A tire mark on a built line is the most valuable place for
  // one, so it has to composite over the tread rather than under it.
  mesh.renderOrder = 5;
  mesh.visible = false;
}

// ---------------------------------------------------------------------------
// Sweeping
// ---------------------------------------------------------------------------

function surfIndex(s: Surface): number {
  const i = s as number;
  return i >= 0 && i < TRACK_TAKE.length ? i : 0;
}

/** Strength of the mark this wheel is making right now, 0..1. */
function markStrength(w: WheelState, wet: number): number {
  const si = surfIndex(w.ground.surface);
  const loadF = clamp01(w.load / (Tuning.bike.mass * G));
  // A sliding tire scrapes a far more visible line than a rolling one, and a lightly
  // loaded tire barely marks at all. Both come straight out of the solver.
  //
  // The slip term has a high floor on purpose. A tire does not need to be sliding to cut
  // a rut — a loaded tire rolling through soft ground displaces it — and gating hard on
  // slip is how the first pass ended up with nothing under the wheel through the whole
  // straight-line half of a run. Slip still nearly doubles the mark, which is what makes
  // a drift visibly darker than a roll.
  const slip = clamp01(w.slipUsage);
  return clamp01(
    TRACK_TAKE[si] * (0.62 + 0.58 * loadF) * (0.82 + 0.40 * slip) * (1 + 0.25 * wet),
  );
}

function liftPen(p: Pen): void {
  p.down = false;
  p.onGround = false;
  p.tipStrength = 0;
}

/** Start a new ribbon at the wheel's current patch without emitting anything. */
function setPen(p: Pen, w: WheelState, hw: number, strength: number, wet: number): void {
  const si = surfIndex(w.ground.surface);
  const dry = TRACK_TINT[si];
  const wt = TRACK_TINT_WET[si];
  p.cr = lerp(dry[0], wt[0], wet);
  p.cg = lerp(dry[1], wt[1], wet);
  p.cb = lerp(dry[2], wt[2], wet);
  p.ca = strength;
  p.x = w.cx; p.y = w.cy; p.z = w.cz;
  const nx = w.ground.nx, ny = w.ground.ny, nz = w.ground.nz;
  p.nx = nx; p.ny = ny; p.nz = nz;
  // Lateral for the opening cross-section, built from the last known travel direction.
  // It has to use the *same* handedness as sweep() — cross(dir, normal) — or the first
  // quad after every pen-down comes out as a bowtie, and pen-downs happen on every
  // landing.
  let ex = -p.dz * ny;
  let ey = p.dz * nx - p.dx * nz;
  let ez = p.dx * ny;
  const el = Math.hypot(ex, ey, ez) || 1;
  ex /= el; ey /= el; ez /= el;
  p.ex = ex; p.ey = ey; p.ez = ez;
  p.hw = hw;
  p.lx = w.cx + ex * hw; p.ly = w.cy + ey * hw; p.lz = w.cz + ez * hw;
  p.rx = w.cx - ex * hw; p.ry = w.cy - ey * hw; p.rz = w.cz - ez * hw;
  p.down = true;
}

function writeVert(vi: number, x: number, y: number, z: number,
  u: number, v: number, hw: number,
  nx: number, ny: number, nz: number,
  r: number, g: number, b: number, a: number): void {
  const p3 = vi * 3;
  posArr[p3] = x; posArr[p3 + 1] = y; posArr[p3 + 2] = z;
  nrmArr[p3] = nx; nrmArr[p3 + 1] = ny; nrmArr[p3 + 2] = nz;
  const c4 = vi * 4;
  colArr[c4] = r; colArr[c4 + 1] = g; colArr[c4 + 2] = b; colArr[c4 + 3] = a;
  uvArr[p3] = u; uvArr[p3 + 1] = v; uvArr[p3 + 2] = hw;
}

/**
 * Commit one quad, from the pen's stored cross-section to the patch at (tx,ty,tz), and
 * advance the pen onto it.
 *
 * Split out of `sweep` so that the two places a segment can be born share one body: the
 * ordinary step, and the **flush** when the pen lifts. The flush is what stops a short
 * touchdown leaving nothing behind — a wheel that kisses the ground for 0.2 m between two
 * chatter hops never travels MIN_STEP, so before this every one of those contacts emitted
 * exactly zero quads and the permanent mark simply skipped them.
 */
function commitQuad(
  p: Pen,
  tx: number, ty: number, tz: number,
  nx: number, ny: number, nz: number,
  hw: number, strength: number,
  cr: number, cg: number, cb: number,
): void {
  const dx = tx - p.x;
  const dy = ty - p.y;
  const dz = tz - p.z;
  const d = Math.hypot(dx, dz);
  let ex = dy * nz - dz * ny;
  let ey = dz * nx - dx * nz;
  let ez = dx * ny - dy * nx;
  const el = Math.hypot(ex, ey, ez);
  if (el < 1e-5) return;
  ex /= el; ey /= el; ez /= el;

  const arc0 = p.arc;
  const arc1 = p.arc + d;
  const seg = cursor;
  const v = seg * 4;
  // Leading edge is the previous cross-section verbatim — shared exactly, so there is no
  // crack between segments however hard the line turns.
  writeVert(v + 0, p.lx, p.ly, p.lz, -1, arc0, p.hw,
    p.nx, p.ny, p.nz, p.cr, p.cg, p.cb, p.ca);
  writeVert(v + 1, p.rx, p.ry, p.rz, 1, arc0, p.hw,
    p.nx, p.ny, p.nz, p.cr, p.cg, p.cb, p.ca);
  const rX = tx - ex * hw, rY = ty - ey * hw, rZ = tz - ez * hw;
  const lX = tx + ex * hw, lY = ty + ey * hw, lZ = tz + ez * hw;
  writeVert(v + 2, rX, rY, rZ, 1, arc1, hw, nx, ny, nz, cr, cg, cb, strength);
  writeVert(v + 3, lX, lY, lZ, -1, arc1, hw, nx, ny, nz, cr, cg, cb, strength);

  markDirty(seg);
  cursor = RING_START + ((cursor + 1 - RING_START) % RING_CAP);
  if (written < RING_CAP) written++;

  p.x = tx; p.y = ty; p.z = tz;
  p.lx = lX; p.ly = lY; p.lz = lZ;
  p.rx = rX; p.ry = rY; p.rz = rZ;
  p.nx = nx; p.ny = ny; p.nz = nz;
  p.ex = ex; p.ey = ey; p.ez = ez;
  p.hw = hw;
  p.cr = cr; p.cg = cg; p.cb = cb; p.ca = strength;
  if (d > 1e-5) { p.dx = dx / d; p.dz = dz / d; }
  // Wrapped between quads, never inside one, and by a whole number of knob periods — so
  // the pattern is continuous across the wrap and the coordinate never loses precision.
  p.arc = arc1 >= ARC_WRAP ? arc1 - ARC_WRAP : arc1;
}

/**
 * Bank whatever the live tip is currently showing as a permanent quad, then lift.
 *
 * Called on every pen-up. Without it the mark a rider leaves is only the part of his line
 * that happened to be longer than MIN_STEP between two losses of contact — which on this
 * terrain at speed is a minority of it.
 */
function flushPen(p: Pen): void {
  if (p.down && p.tipStrength >= 0.04) {
    const dx = p.tx - p.x;
    const dz = p.tz - p.z;
    if (dx * dx + dz * dz > 0.0025) {
      commitQuad(p, p.tx, p.ty, p.tz, p.tnx, p.tny, p.tnz,
        p.tipHw, p.tipStrength, p.tcr, p.tcg, p.tcb);
    }
  }
  liftPen(p);
}

/**
 * Sweep one wheel. Emits at most one quad per call; at 240 Hz and 19 m/s a wheel covers
 * 79 mm per step, so one is always enough.
 */
function sweep(p: Pen, w: WheelState, wet: number): void {
  // Ground crossed since the last step, measured under the wheel. `cx/cz` is published
  // before the contact test in the solver, so it keeps tracking the hillside beneath a
  // wheel that is off it — which is exactly the quantity AIR_BRIDGE is a bound on.
  const stepM = p.hasA ? Math.hypot(w.cx - p.ax, w.cz - p.az) : 0;
  p.ax = w.cx; p.az = w.cz; p.hasA = true;

  if (!w.contact) {
    // A wheel genuinely in the air is not writing, and breaking the ribbon is what makes
    // a gap appear across a double instead of a stripe painted through the sky. But
    // `contact` is `travel > 0` — a knife edge that flickers many times a second over
    // chatter — and breaking on every flicker is what left the mark more gap than mark.
    // Carry the pen across a skip; break it for a jump. Nothing is emitted either way,
    // so the bridged quad spans ground the tyre really did cross.
    p.airM += stepM;
    p.onGround = false;
    if (p.airM > AIR_BRIDGE) flushPen(p);
    return;
  }
  p.airM = 0;

  const strength = markStrength(w, wet);
  const slip = clamp01(w.slipUsage);
  // Quad half-width. The rut occupies RUT_FRAC of it; the rest is the displaced lip.
  const hw = (RUT_HALF + RUT_HALF_SLIP * slip * slip) / RUT_FRAC;

  if (strength < 0.04) {
    flushPen(p);
    return;
  }

  const si = surfIndex(w.ground.surface);
  const dry = TRACK_TINT[si];
  const wt = TRACK_TINT_WET[si];
  const cr = lerp(dry[0], wt[0], wet);
  const cg = lerp(dry[1], wt[1], wet);
  const cb = lerp(dry[2], wt[2], wet);

  // What the live tip should draw itself at, and what a flush would bank. Set before the
  // early returns below, because the whole point of the tip is that it is current even on
  // the steps where no quad is committed — which is most of them.
  p.onGround = true;
  p.tipHw = hw;
  p.tipStrength = strength;
  p.tx = w.cx; p.ty = w.cy; p.tz = w.cz;
  p.tnx = w.ground.nx; p.tny = w.ground.ny; p.tnz = w.ground.nz;
  p.tcr = cr; p.tcg = cg; p.tcb = cb;

  if (!p.down) {
    setPen(p, w, hw, strength, wet);
    return;
  }

  const dx = w.cx - p.x;
  const dz = w.cz - p.z;
  const d = Math.hypot(dx, dz);
  if (d < MIN_STEP) return;

  // Turn since the last segment: cross product of the two unit headings.
  const turn = Math.abs(p.dx * (dz / d) - p.dz * (dx / d));
  if (d < MAX_STEP && turn < TURN_STEP) return;

  commitQuad(p, w.cx, w.cy, w.cz, w.ground.nx, w.ground.ny, w.ground.nz,
    hw, strength, cr, cg, cb);
}

/**
 * Collapse a quad to a point. Four coincident vertices rasterise nothing at all, which is
 * how an unused tip costs zero fill rather than a full-width transparent quad the depth
 * sort still has to carry. The normal is left as +Y because the vertex shader takes
 * `normalize(aNrm)` unconditionally and a zero normal there is a NaN.
 */
function collapseQuad(seg: number): void {
  const v = seg * 4;
  for (let k = 0; k < 4; k++) writeVert(v + k, 0, 0, 0, 0, 0, 0.15, 0, 1, 0, 0, 0, 0, 0);
}

/**
 * Rewrite one wheel's live tip: the quad that runs from the pen's last committed
 * cross-section to wherever the contact patch is *right now*.
 *
 * This is the whole of the R5 fix. Without it the newest cross-section trails the tyre by
 * up to MIN_STEP, and on a chase camera the contact patch is at the far end of the only
 * wedge of already-ridden ground the lens can see — so the part of the mark best placed to
 * be seen was the part that had not been committed yet. Called once per rendered frame
 * from `update()`, so it costs eight vertex writes and a 96-byte upload, not a re-sweep.
 *
 * Returns whether it drew anything.
 */
function writeTip(p: Pen, seg: number): boolean {
  if (!p.down || !p.onGround || p.tipStrength < 0.04) {
    collapseQuad(seg);
    return false;
  }
  const dx = p.tx - p.x;
  const dy = p.ty - p.y;
  const dz = p.tz - p.z;
  const d = Math.hypot(dx, dz);
  // The pen was just set at this patch — there is genuinely no ground between the last
  // cross-section and the tyre yet, and inventing some would paint over dirt the wheel has
  // not reached.
  if (d < 0.02) {
    collapseQuad(seg);
    return false;
  }

  const nx = p.tnx, ny = p.tny, nz = p.tnz;
  let ex = dy * nz - dz * ny;
  let ey = dz * nx - dx * nz;
  let ez = dx * ny - dy * nx;
  const el = Math.hypot(ex, ey, ez);
  if (el < 1e-5) {
    collapseQuad(seg);
    return false;
  }
  ex /= el; ey /= el; ez /= el;

  const hw = p.tipHw;
  const v = seg * 4;
  const arc0 = p.arc;
  const arc1 = p.arc + d;
  // Leading edge is the pen's stored cross-section verbatim, exactly as a committed quad
  // would use it, so the tip shares an edge with the ribbon and there is no crack at the
  // join and no double-darkened overlap.
  writeVert(v + 0, p.lx, p.ly, p.lz, -1, arc0, p.hw,
    p.nx, p.ny, p.nz, p.cr, p.cg, p.cb, p.ca);
  writeVert(v + 1, p.rx, p.ry, p.rz, 1, arc0, p.hw,
    p.nx, p.ny, p.nz, p.cr, p.cg, p.cb, p.ca);
  writeVert(v + 2, p.tx - ex * hw, p.ty - ey * hw, p.tz - ez * hw, 1, arc1, hw,
    nx, ny, nz, p.tcr, p.tcg, p.tcb, p.tipStrength);
  writeVert(v + 3, p.tx + ex * hw, p.ty + ey * hw, p.tz + ez * hw, -1, arc1, hw,
    nx, ny, nz, p.tcr, p.tcg, p.tcb, p.tipStrength);
  return true;
}

function clearAll(): void {
  colArr.fill(0);
  collapseQuad(0);
  collapseQuad(1);
  cursor = RING_START;
  written = 0;
  tipsLive = false;
  dirtyN = 0;
  dirtyAll = true;
  liftPen(penRear);
  liftPen(penFront);
  penRear.hasA = false;
  penFront.hasA = false;
  penRear.airM = 0;
  penFront.airM = 0;
}

let lastX = 0;
let lastZ = 0;
let hadPos = false;
/**
 * Probe latch, never set in play. `mesh.visible` is rewritten from the live counts every
 * frame, so an A/B that pokes the flag directly is overwritten before it draws — which is
 * how three rounds of "is the ribbon on screen" got answered by argument rather than by a
 * pair of frames and a pixel count.
 */
let forceHidden = false;

// ---------------------------------------------------------------------------

registerSystem({
  id: 'tracks',
  order: 101,

  init(ctx: SystemContext) {
    // Whoever gets here first installs the one system that pulls relief.ts's shared light
    // block into step with the sky each frame. Idempotent by design — every consumer may
    // call it and only the first does anything.
    installReliefSystem();
    build();
    if (mesh) ctx.scene.add(mesh);
  },

  fixedUpdate(_dt: number, ctx: SystemContext) {
    const b = ctx.bike;

    // Reset / teleport: the ribbon must not be dragged across the mountain, and the old
    // track is no longer somewhere the rider has been.
    if (hadPos) {
      const dx = b.x - lastX;
      const dz = b.z - lastZ;
      if (dx * dx + dz * dz > 400) clearAll();
    }
    lastX = b.x; lastZ = b.z;
    hadPos = true;

    const wet = clamp01(Weather.wetness);
    sweep(penRear, b.rear, wet);
    sweep(penFront, b.front, wet);

    // The floor's gate. It is the SIM's statement that the rider is laying a mark down —
    // wheel on the ground, moving — not the mesh's statement that it drew one, for the same
    // reason particles.ts gates its presence floor on `roostDemand`: a floor that reads the
    // thing it is checking is satisfied by an effect that is running and invisible.
    if (b.rear.contact && b.speed > TRACK_PRESENCE_SPEED) rollHeldS += _dt;
    else rollHeldS = 0;
  },

  update(dt: number, ctx: SystemContext) {
    if (!geo || !mesh || !mat || !attrPos || !attrCol || !attrUV || !attrNrm) return;

    // The two live tips: the quads that carry the ribbon the rest of the way to the
    // contact patch. Rewritten every frame from the pens' committed edges, which is why
    // they are outside the ring and why their upload range is a fixed 8 vertices.
    const tipR = writeTip(penRear, 0);
    const tipF = writeTip(penFront, 1);
    tipsLive = tipR || tipF;

    if (written === 0 && !tipsLive) {
      mesh.visible = false;
      dirtyN = 0;
      dirtyAll = false;
      return;
    }
    mesh.visible = !forceHidden;
    geo.setDrawRange(0, (RING_START + written) * 6);

    if (dirtyAll) {
      attrPos.clearUpdateRanges(); attrPos.needsUpdate = true;
      attrCol.clearUpdateRanges(); attrCol.needsUpdate = true;
      attrUV.clearUpdateRanges(); attrUV.needsUpdate = true;
      attrNrm.clearUpdateRanges(); attrNrm.needsUpdate = true;
    } else {
      attrPos.clearUpdateRanges();
      attrCol.clearUpdateRanges();
      attrUV.clearUpdateRanges();
      attrNrm.clearUpdateRanges();
      // The tips, always: they are rewritten every frame by construction. Eight vertices,
      // which is 96 bytes of position and 128 of colour — the price of the mark reaching
      // the tyre, and it is nothing.
      attrPos.addUpdateRange(0, TIP_SEGMENTS * 12);
      attrCol.addUpdateRange(0, TIP_SEGMENTS * 16);
      attrUV.addUpdateRange(0, TIP_SEGMENTS * 12);
      attrNrm.addUpdateRange(0, TIP_SEGMENTS * 12);
      for (let k = 0; k < dirtyN; k++) {
        const v0 = dirtySeg[k] * 4;
        attrPos.addUpdateRange(v0 * 3, 12);
        attrCol.addUpdateRange(v0 * 4, 16);
        attrUV.addUpdateRange(v0 * 3, 12);
        attrNrm.addUpdateRange(v0 * 3, 12);
      }
      attrPos.needsUpdate = true;
      attrCol.needsUpdate = true;
      attrUV.needsUpdate = true;
      attrNrm.needsUpdate = true;
    }
    dirtyN = 0;
    dirtyAll = false;

    // --- self-audit: is the mark IN THE PICTURE ------------------------------
    //
    // See INV_TRACK_SCREEN. The quads are in world space and the mesh transform is
    // identity, so the projection is six dot products per vertex against the camera's own
    // basis — no matrix object, no allocation, and it tracks a chase rig that swings its
    // FOV from 62 to 88 degrees with nothing to keep in sync. Area comes from the shoelace
    // of the four projected corners in NDC, which is exact for the quad as drawn.
    ctx.camera.updateMatrixWorld();
    const cme = ctx.camera.matrixWorld.elements;
    const rgX = cme[0], rgY = cme[1], rgZ = cme[2];
    const upX = cme[4], upY = cme[5], upZ = cme[6];
    const bkX = cme[8], bkY = cme[9], bkZ = cme[10];
    const pme = ctx.camera.projectionMatrix.elements;
    const projX = pme[0];
    const projY = pme[5];
    const camX = ctx.camera.position.x;
    const camY = ctx.camera.position.y;
    const camZ = ctx.camera.position.z;
    let cov = 0;
    const scan = Math.min(TRACK_SCAN_SEGS, written);
    for (let k = 0; k < scan + TIP_SEGMENTS; k++) {
      // The two live tips first — they are the part of the ribbon nearest the tyre and
      // therefore the part the wedge between lens and bike is mostly made of.
      const seg = k < TIP_SEGMENTS
        ? k
        : RING_START + (((cursor - RING_START - (k - TIP_SEGMENTS) - 1) % RING_CAP) + RING_CAP) % RING_CAP;
      const v0 = seg * 4;
      // A quad with no strength in it is not drawn; the tips carry alpha 0 when the pen is
      // up, and counting those would report a mark that is not there.
      if (colArr[v0 * 4 + 3] < 0.02 && colArr[(v0 + 2) * 4 + 3] < 0.02) continue;
      let ndcX0 = 0, ndcY0 = 0;
      let ok = true;
      let area = 0;
      for (let c = 0; c < 4 && ok; c++) {
        const p3 = (v0 + c) * 3;
        const dx = posArr[p3] - camX;
        const dy = posArr[p3 + 1] - camY;
        const dz = posArr[p3 + 2] - camZ;
        const depth = -(dx * bkX + dy * bkY + dz * bkZ);
        // Any corner behind the lens: skip the quad rather than project a reflected one.
        // It is the conservative direction for a FLOOR — a partly-visible quad is counted
        // as absent, so this can only ever under-report presence.
        if (depth < 0.05) { ok = false; break; }
        const nx = ((dx * rgX + dy * rgY + dz * rgZ) * projX) / depth;
        const ny = ((dx * upX + dy * upY + dz * upZ) * projY) / depth;
        if (c === 0) { ndcX0 = nx; ndcY0 = ny; }
        tmpNdcX[c] = nx; tmpNdcY[c] = ny;
      }
      if (!ok) continue;
      // Shoelace, and then clipped to the frame: a quad that is 90 % off-screen must not
      // report its whole area. Cheap and conservative — the bounding box of the quad
      // intersected with the view rectangle, scaled by the quad's fill of its own box.
      for (let c = 0; c < 4; c++) {
        const d = (c + 1) & 3;
        area += tmpNdcX[c] * tmpNdcY[d] - tmpNdcX[d] * tmpNdcY[c];
      }
      area = Math.abs(area) * 0.5;
      if (area < 1e-9) continue;
      let bx0 = tmpNdcX[0], bx1 = tmpNdcX[0], by0 = tmpNdcY[0], by1 = tmpNdcY[0];
      for (let c = 1; c < 4; c++) {
        if (tmpNdcX[c] < bx0) bx0 = tmpNdcX[c];
        if (tmpNdcX[c] > bx1) bx1 = tmpNdcX[c];
        if (tmpNdcY[c] < by0) by0 = tmpNdcY[c];
        if (tmpNdcY[c] > by1) by1 = tmpNdcY[c];
      }
      const ix = Math.max(0, Math.min(bx1, 1) - Math.max(bx0, -1));
      const iy = Math.max(0, Math.min(by1, 1) - Math.max(by0, -1));
      const box = (bx1 - bx0) * (by1 - by0);
      if (box < 1e-9) continue;
      // NDC spans -1..1 on both axes, so the frame is 4 square units of it.
      cov += (area * (ix * iy)) / box / 4;
      void ndcX0; void ndcY0;
    }
    onScreenFrac = cov;
    if (rollHeldS > TRACK_PRESENCE_LAG) {
      if (cov < INV_TRACK_SCREEN.worst || INV_TRACK_SCREEN.samples === 0) {
        INV_TRACK_SCREEN.observeWhere(cov,
          `${(ctx.bike.speed * 3.6).toFixed(0)} km/h, ${written} segs, lens `
          + `${(camY - ctx.bike.y).toFixed(2)} m over chassis`);
      } else {
        INV_TRACK_SCREEN.observe(cov);
      }
      // ...and how far short of the tyre the drawn mark stops. See INV_TRACK_GAP.
      const gdx = penRear.tx - ctx.bike.rear.cx;
      const gdz = penRear.tz - ctx.bike.rear.cz;
      INV_TRACK_GAP.observe(penRear.down ? Math.hypot(gdx, gdz) : 0);
    }
    void dt;

    // The sun direction, its radiance, the sky and the ground bounce all arrive through
    // relief.ts's shared block and are written once a frame for the whole scene, so there
    // is nothing left here to copy. What is left is the two things that are genuinely
    // this material's own.
    const sky = activeSky.current;
    const u = mat.uniforms;
    if (sky) {
      // Wet dirt is glossier, and so is a wet compacted track. Fade the whole term out
      // with the sun so a dusk track does not glint at nothing.
      u.uSheen.value = SHEEN_BASE * (1 + Weather.wetness * 2.0)
        * clamp01(sky.sunElevation * 4);
      // With the sun on the deck there is no cross-light left to shape the groove, so the
      // light-independent term — how much sky the rut has lost to its own wall — has to
      // carry more of it. Wet ground holds a deeper rut, too.
      u.uOcc.value = RUT_OCC + 0.18 * (1 - clamp01(sky.sunElevation * 3));
      u.uDepthM.value = RUT_DEPTH * (1 + 0.25 * Weather.wetness);
    }
  },

  dispose() {
    geo?.dispose();
    mat?.dispose();
  },
});

/** Diagnostics for the capture harness. */
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__gripTracks = {
    segments: () => written,
    capacity: TRACK_SEGMENTS,
    clear: clearAll,
    /** Live uniform poke, so bias and contrast can be tuned without a rebuild. */
    set(name: string, value: number) {
      const u = mat?.uniforms[name];
      if (u && typeof u.value === 'number') u.value = value;
    },
    /**
     * Override the distance fade. Exists so the far field can be *checked* rather than
     * argued about: push the fade out to a kilometre and the ribbon at 300 m either lies
     * on the hillside or floats off it, and the screenshot settles which.
     */
    far(start: number, end: number, cull: number) {
      if (!mat) return;
      (mat.uniforms.uFade.value as THREE.Vector2).set(start, end);
      mat.uniforms.uCull.value = cull;
    },
    /** A/B latch for probes. Survives the per-frame rewrite of `mesh.visible`. */
    show(v: boolean) { forceHidden = !v; if (mesh) mesh.visible = v && (written > 0 || tipsLive); },
    /**
     * Committed ring segments, live tips, the ring's capacity, and whether the mark is
     * being lit by the same analytic sun occlusion the ground is. `skyWired: false` means
     * every rut is lit as though it stood in the open — checkable from a capture.
     */
    /**
     * Last frame's measured share of the frame covered by recently-laid mark, plus the
     * gate that decides whether the floor is entitled to observe it. See INV_TRACK_SCREEN —
     * exposed so a pixel probe can be checked against the geometric number rather than
     * either being trusted alone.
     */
    onScreen: () => ({ frac: onScreenFrac, rollHeldS, written }),
    debug: () => ({ written, tipsLive, cap: RING_CAP, skyWired }),
  };
}

export {};
